import React, { useState } from 'react';
import { Plane, MapPin, Clock, FileText, Package, Trash2, X, Car, Users } from 'lucide-react';

// Car Travel Tooltip Component (same structure as TravelTooltip)
export const CarTravelTooltip = ({ carTravel, onDelete, onClose }) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const formatTime = (timeString) => {
    return timeString || 'Not specified';
  };

  // Check if address is auto-generated (not real)
  const isAutoGeneratedAddress = (address) => {
    if (!address) return true;
    const autoPatterns = [
      'Travel check-in',
      'Travel check-out',
      'Travel Check-in',
      'Travel Check-out',
      'Check-in -',
      'Check-out -',
      'Airport to accommodation',
      'Accommodation to airport'
    ];
    return autoPatterns.some(pattern => address.includes(pattern));
  };

  // Get clean address (null if auto-generated)
  const getCleanAddress = (travel) => {
    if (!travel.address || isAutoGeneratedAddress(travel.address)) {
      return null;
    }
    return travel.address;
  };

  // Get travel status: 'pending', 'draft', 'sent'
  const getTravelStatus = (travel) => {
    const hasTransport = !!travel.transport_type;
    const hasValidAddress = !!getCleanAddress(travel);
    const isEmailSent = travel.email_sent === true;

    if (!hasTransport || !hasValidAddress) {
      return 'pending'; // Not configured yet
    }

    if (hasTransport && hasValidAddress && !isEmailSent) {
      return 'draft'; // Configured but not sent
    }

    return 'sent'; // Configured and sent
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(carTravel.id);
    }
    setShowConfirmDelete(false);
    if (onClose) {
      onClose();
    }
  };

  if (showConfirmDelete) {
    return (
      <div className="bg-white border border-steel-200 rounded-xl shadow-lg p-4 min-w-80 max-w-96">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-red-600 flex items-center">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Car Travel
          </h3>
          <button
            onClick={() => setShowConfirmDelete(false)}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="text-slate-700 mb-4">
          Are you sure you want to delete this car travel for <strong>{carTravel.car.car_brand} {carTravel.car.car_model}</strong>?
          <div className="mt-2 p-2 bg-steel-50 rounded border">
            <div className="font-medium space-y-1">
              {carTravel.street_address && <div>{carTravel.street_address}</div>}
              <div className="flex flex-wrap gap-x-2">
                {carTravel.city && <span>{carTravel.city}</span>}
                {carTravel.post_code && <span>{carTravel.post_code}</span>}
              </div>
              {carTravel.country && <div>{carTravel.country}</div>}
              {!carTravel.street_address && !carTravel.city && !carTravel.country && !carTravel.post_code && carTravel.address && (
                <div>{carTravel.address}</div>
              )}
            </div>
            <div className="text-xs text-slate-600">
              {carTravel.travelType === 'check_in' ? 'Check-in' : 'Check-out'} • {formatTime(carTravel.startTime)}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowConfirmDelete(false)}
            className="px-4 py-2 text-slate-600 border border-steel-300 rounded hover:bg-steel-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-steel-200 rounded-xl shadow-lg p-4 min-w-80 max-w-96">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900 flex items-center">
          <Car className="h-4 w-4 mr-2 text-brand-600" />
          Car {carTravel.travelType === 'check_in' ? 'Check-in' : 'Check-out'}
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowConfirmDelete(true)}
            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
            title="Delete car travel"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {/* Car Info */}
        <div className="flex items-start">
          <Car className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-slate-700 font-medium">Vehicle</div>
            <div className="text-slate-900">{carTravel.car.car_brand} {carTravel.car.car_model}</div>
            <div className="text-slate-600 text-sm">{carTravel.car.plate_number}</div>
          </div>
        </div>

        {/* Passengers */}
        <div className="flex items-start">
          <Users className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-slate-700 font-medium">Passengers</div>
            <div className="text-slate-900">
              {carTravel.passengersData.map(p => `${p.first_name} ${p.last_name}`).join(', ')}
            </div>
          </div>
        </div>

        {/* Destination */}
        {(carTravel.street_address || carTravel.city || carTravel.country || carTravel.post_code || carTravel.address) && (
          <div className="flex items-start">
            <MapPin className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-slate-700 font-medium">Destination</div>
              <div className="text-slate-900 space-y-1">
                {carTravel.street_address && (
                  <div>{carTravel.street_address}</div>
                )}
                <div className="flex flex-wrap gap-x-2">
                  {carTravel.city && <span>{carTravel.city}</span>}
                  {carTravel.post_code && <span>{carTravel.post_code}</span>}
                </div>
                {carTravel.country && (
                  <div>{carTravel.country}</div>
                )}
                {!carTravel.street_address && !carTravel.city && !carTravel.country && !carTravel.post_code && carTravel.address && (
                  <div>{carTravel.address}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Schedule */}
        <div className="flex items-start">
          <Clock className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-slate-700 font-medium">Time</div>
            <div className="text-slate-900">{formatTime(carTravel.startTime)}</div>
          </div>
        </div>

        {/* Notes */}
        {carTravel.notes && (
          <div className="flex items-start">
            <FileText className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-slate-700 font-medium">Notes</div>
              <div className="text-slate-900 whitespace-pre-wrap">{carTravel.notes}</div>
            </div>
          </div>
        )}

        {/* Status */}
        <div className="pt-2 border-t border-steel-100">
          <div className="text-xs flex items-center justify-between">
            <span className="text-slate-500">Status:</span>
            {(() => {
              const status = getTravelStatus(carTravel);
              if (status === 'sent') {
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Sent
                  </span>
                );
              } else if (status === 'draft') {
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                    📝 Draft
                  </span>
                );
              } else {
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-steel-100 text-slate-600">
                    ⏳ Pending
                  </span>
                );
              }
            })()}
          </div>
          {carTravel.email_sent_at && (
            <div className="text-xs text-slate-500 mt-1">
              Sent: {new Date(carTravel.email_sent_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TravelTooltip = ({ travel, user, car, onDelete, onClose }) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Check if address is auto-generated (not real)
  const isAutoGeneratedAddress = (address) => {
    if (!address) return true;
    const autoPatterns = [
      'Travel check-in',
      'Travel check-out',
      'Travel Check-in',
      'Travel Check-out',
      'Airport to accommodation',
      'Accommodation to airport'
    ];
    return autoPatterns.some(pattern => address.includes(pattern));
  };

  // Get clean address (null if auto-generated)
  const getCleanAddress = (travel) => {
    if (!travel.address || isAutoGeneratedAddress(travel.address)) {
      return null;
    }
    return travel.address;
  };

  // Get travel status: 'pending', 'draft', 'sent'
  const getTravelStatus = (travel) => {
    const hasTransport = !!travel.transport_type;
    const hasValidAddress = !!getCleanAddress(travel);
    const isEmailSent = travel.email_sent === true;

    if (!hasTransport || !hasValidAddress) {
      return 'pending'; // Not configured yet
    }

    if (hasTransport && hasValidAddress && !isEmailSent) {
      return 'draft'; // Configured but not sent
    }

    return 'sent'; // Configured and sent
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(travel.id);
    }
    setShowConfirmDelete(false);
    if (onClose) {
      onClose();
    }
  };

  if (showConfirmDelete) {
    const isCar = car || travel.car || travel.car_id;

    return (
      <div className="bg-white border border-steel-200 rounded-xl shadow-lg p-4 min-w-80 max-w-96">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-red-600 flex items-center">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete {isCar ? 'Car ' : ''}Travel
          </h3>
          <button
            onClick={() => setShowConfirmDelete(false)}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="text-sm text-slate-700 mb-4">
          Are you sure you want to delete this travel for <strong>
            {isCar
              ? `${car?.car_brand || travel.car?.car_brand || 'Unknown'} ${car?.car_model || travel.car?.car_model || 'Car'}`
              : `${user?.first_name} ${user?.last_name}`
            }
          </strong>?
          <div className="mt-2 p-2 bg-steel-50 rounded border">
            <div className="font-medium">{travel.address}</div>
            <div className="text-xs text-slate-600">
              {formatDate(travel.start_date)} • {formatTime(travel.start_date)} - {formatTime(travel.end_date)}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => setShowConfirmDelete(false)}
            className="px-3 py-1 text-sm text-slate-600 border border-steel-300 rounded hover:bg-steel-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete Travel
          </button>
        </div>
      </div>
    );
  }

  const isCar = car || travel.car || travel.car_id;

  return (
    <div className="bg-white border border-steel-200 rounded-xl shadow-lg p-4 min-w-80 max-w-96">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-brand-600 flex items-center">
          {isCar ? <Car className="h-4 w-4 mr-2" /> : <Plane className="h-4 w-4 mr-2" />}
          {isCar ? 'Car ' : ''}Travel Details
        </h3>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setShowConfirmDelete(true)}
            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            title="Delete travel"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3 text-sm">
        {/* Person or Car */}
        {isCar ? (
          <div className="flex items-start">
            <Car className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-slate-700 font-medium">Vehicle</div>
              <div className="font-medium text-slate-900">
                {car?.car_brand || travel.car?.car_brand || 'Unknown'} {car?.car_model || travel.car?.car_model || 'Car'}
              </div>
              {(car?.plate_number || travel.car?.plate_number) && (
                <div className="text-slate-600 text-xs">{car?.plate_number || travel.car?.plate_number}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-start">
            <div className="w-5 h-5 bg-brand-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
              <span className="text-xs font-medium text-brand-600">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div>
              <div className="font-medium text-slate-900">
                {user?.first_name} {user?.last_name}
              </div>
            </div>
          </div>
        )}

        {/* Destination */}
        {travel.address && (
          <div className="flex items-start">
            <MapPin className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-slate-700 font-medium">Destination</div>
              <div className="text-slate-900">{travel.address}</div>
            </div>
          </div>
        )}

        {/* Date and Time */}
        <div className="flex items-start">
          <Clock className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-slate-700 font-medium">Schedule</div>
            <div className="text-slate-900">
              {formatDate(travel.start_date)}
            </div>
            <div className="text-slate-600 text-xs">
              {formatTime(travel.start_date)} - {formatTime(travel.end_date)}
            </div>
          </div>
        </div>

        {/* Description */}
        {travel.description && (
          <div className="flex items-start">
            <FileText className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-slate-700 font-medium">Description</div>
              <div className="text-slate-900 whitespace-pre-wrap">{travel.description}</div>
            </div>
          </div>
        )}

        {/* Required Items */}
        {travel.required_items && (
          <div className="flex items-start">
            <Package className="h-4 w-4 text-slate-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-slate-700 font-medium">Required Items</div>
              <div className="text-slate-900 whitespace-pre-wrap">{travel.required_items}</div>
            </div>
          </div>
        )}

        {/* Status */}
        <div className="pt-2 border-t border-steel-100">
          <div className="text-xs flex items-center justify-between">
            <span className="text-slate-500">Status:</span>
            {(() => {
              const status = getTravelStatus(travel);
              if (status === 'sent') {
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Sent
                  </span>
                );
              } else if (status === 'draft') {
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                    📝 Draft
                  </span>
                );
              } else {
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-steel-100 text-slate-600">
                    ⏳ Pending
                  </span>
                );
              }
            })()}
          </div>
          {travel.email_sent_at && (
            <div className="text-xs text-slate-500 mt-1">
              Sent: {new Date(travel.email_sent_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelTooltip;